import { Fragment, useState } from "react";
import htmlParser from "html-react-parser";
import { useSelector } from "react-redux";
import { Draggable, Droppable } from "react-beautiful-dnd";
// Components
import ImagePicker from "./ImagePicker";
import { ELEMENTS } from "./Element";
// Auto Sizes
import AutowidthInput from "react-autowidth-input";
import TextareaAutosize from "react-textarea-autosize";
// Ace
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/theme-monokai";
// Quill
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
// Actions
import {
  setView,
  removeView,
  addLine,
  removeLine,
  moveLine,
  builderMode,
} from "../redux/actions";

/**
 * Constants
 */
export const LANGUAGES = [
  "javascript",
  "java",
  "python",
  "xml",
  "ruby",
  "sass",
  "markdown",
  "mysql",
  "json",
  "html",
  "handlebars",
  "golang",
  "csharp",
  "elixir",
  "typescript",
  "css",
];

export const COLORS = ["primary", "success", "info", "warning", "danger"];

export const SIZES = ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

/**
 * Input
 */
class InputProps {
  placeholder?: string;
  type?: "text" | "number";
  align?: "left" | "center" | "right";
  value?: string;
  onChange?: (value: string) => void;
}
const Input = ({
  placeholder = "",
  type = "text",
  align = "left",
  value = "",
  onChange = () => {},
}: InputProps): any => {
  return (
    <AutowidthInput
      placeholder={placeholder}
      type={type}
      value={value}
      className={"builder-input text-" + align}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};
/**
 * Select
 */
class SelectProps {
  value?: any;
  onChange?: (value: string) => void;
  options?: { label: string; value: string }[];
}
const Select = ({
  value = "",
  onChange = () => {},
  options = [],
}: SelectProps): any => {
  return (
    <select
      className="builder-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((opt) => (
        <option value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
};
/**
 * TextArea
 */
class TextAreaProps {
  value?: string;
  onChange?: (value: string) => void;
}
const TextArea = ({ value = "", onChange = () => {} }: TextAreaProps) => {
  return (
    <TextareaAutosize
      className="builder-textarea"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      minRows={3}
    />
  );
};
/**
 * Image
 */
class ImageProps {
  value?: any;
  onChange?: (value: any) => void;
}
const Image = ({ value = "", onChange = () => {} }: ImageProps): any => {
  let [type, setType] = useState(0);
  return (
    <Fragment>
      {type == 0 ? (
        <Fragment>
          <button
            className="btn btn-sm"
            onClick={() => {
              setType(1);
              onChange("");
            }}
          >
            Url ekle
          </button>
        </Fragment>
      ) : (
        <Fragment>
          <button
            className="btn btn-sm"
            onClick={() => {
              setType(0);
              onChange("");
            }}
          >
            Fotoğraf seç
          </button>
          <Input value={value} onChange={(image) => onChange(image)} />
        </Fragment>
      )}
      <ImagePicker.Single
        disabled={type == 1}
        value={value}
        width="100%"
        height="auto"
        fit="contain"
        onChange={(image) => onChange(image.src)}
      />
    </Fragment>
  );
};

/**
 * Table
 */
class TableProps {
  value?: {
    thead: any;
    tbody: any;
  };
  onChange?: (value: any) => void;
}
const Table = ({
  value = { thead: {}, tbody: {} },
  onChange = () => {},
}: TableProps): any => {
  return (
    <div className="table-responsive">
      <table className="table table-bordered">
        <thead>
          <tr>
            {Object.values(value.thead).map((th: any, index: number) => (
              <th>
                <Input
                  value={th}
                  onChange={(val: string) => {
                    value.thead[Object.keys(value.thead)[index]] = val;
                    onChange(value);
                  }}
                />
                <i
                  className="builder-pointer text-danger fa fa-minus"
                  onClick={() => {
                    delete value.thead[Object.keys(value.thead)[index]];
                    Object.keys(value.tbody).map((tr_key: any) => {
                      delete value.tbody[tr_key][
                        Object.keys(value.tbody[tr_key])[index]
                      ];
                    });
                    onChange(value);
                  }}
                />
              </th>
            ))}
            <th>
              <i
                className="builder-pointer text-primary fa fa-plus"
                onClick={() => {
                  value.thead["th$" + getNumber(value.thead, "th", 1)] = "";
                  Object.keys(value.tbody).map((tr_key: string) => {
                    value.tbody[tr_key][
                      "td$" + getNumber(value.tbody[tr_key], "td", 1)
                    ] = "";
                  });
                  onChange(value);
                }}
              />
            </th>
          </tr>
        </thead>
        {Object.values(value.thead).length > 0 ? (
          <tbody>
            {Object.values(value.tbody).map((tr: any, index: number) => (
              <tr>
                {Object.values(tr).map((td: any, td_index: number) => (
                  <td>
                    <Input
                      value={td}
                      onChange={(val: string) => {
                        value.tbody[Object.keys(value.tbody)[index]][
                          Object.keys(tr)[td_index]
                        ] = val;
                        onChange(value);
                      }}
                    />
                  </td>
                ))}
                <td>
                  <i
                    className="builder-pointer text-danger fa fa-minus"
                    onClick={() => {
                      delete value.tbody[Object.keys(value.tbody)[index]];
                      onChange(value);
                    }}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td
                colSpan={Object.values(value.thead).length}
                style={{ width: "100%", textAlign: "center" }}
              >
                <i
                  className="builder-pointer text-primary fa fa-plus"
                  onClick={() => {
                    let new_tr: any = {};
                    Object.keys(value.thead).map(
                      (tr_key: any, index: number) => {
                        new_tr["td$" + (index + 1)] = "";
                      }
                    );
                    value.tbody["tr$" + getNumber(value.tbody, "tr", 1)] =
                      new_tr;
                    onChange(value);
                  }}
                />
              </td>
            </tr>
          </tbody>
        ) : null}
      </table>
    </div>
  );
};

/**
 * Editor
 */
let Block = Quill.import("blots/block");
class AlignBlot extends Block {
  static create(align: any) {
    let node = super.create();
    node.setAttribute("class", "text-" + align);
    return node;
  }
}
AlignBlot.blotName = "align";
Quill.register("formats/align", AlignBlot);

class IndentBlot extends Block {
  static indent = 0;
  static create(value: number) {
    let node = super.create();
    if (value >= 0) {
      node.setAttribute("indent", value);
      node.setAttribute("style", "padding-left: " + 25 * value + "px");
    }
    return node;
  }
  static formats(node: any) {
    return node.getAttribute("indent");
  }
}
IndentBlot.blotName = "indent";
Quill.register("formats/indent", IndentBlot);
class EditorProps {
  value?: string;
  onChange?: (value: string) => void;
}
const Editor = ({
  value = "",
  onChange = (value: string) => {},
}: EditorProps) => {
  return (
    <ReactQuill
      theme="snow"
      className="builder-editor"
      modules={{
        toolbar: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          ["link", "code", { background: [] }, { color: [] }],
          ["blockquote", { align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
        ],
      }}
      value={value}
      onChange={onChange}
    />
  );
};
/**
 * Code
 */

LANGUAGES.forEach((lang) => {
  require(`ace-builds/src-noconflict/mode-${lang}`);
  require(`ace-builds/src-noconflict/snippets/${lang}`);
});

class CodeProps {
  value?: { language: string; value: string };
  onChange?: (value: string) => void;
}
const Code = ({ value, onChange = () => {} }: CodeProps) => {
  if (value)
    return (
      <div className="builder-code">
        <AceEditor
          style={{ width: "100%" }}
          mode={value?.language}
          theme="monokai"
          name="UNIQUE_ID_OF_DIV"
          value={value?.value}
          editorProps={{ $blockScrolling: true }}
          onChange={(code) => onChange(code)}
        />
      </div>
    );
  else return null;
};

/**
 * Area
 */
class AreaProps {
  placeholder?: string;
  align?: "left" | "center" | "right";
  type?:
    | "select"
    | "text"
    | "number"
    | "textarea"
    | "image"
    | "table"
    | "editor"
    | "code";
  children?: any;
  value?: any;
  options?: { value: string; label: string }[];
  path?: string;
}
const Area = ({
  placeholder = "",
  align = "left",
  type = "text",
  children = null,
  value = null,
  options = [],
  path = "",
}: AreaProps) => {
  let { builder = false } = useSelector((state: any) => state.builderReducer);

  const returnValue = (): any => {
    if (value !== null) return value;
    else if (children !== null) return children;
    else return null;
  };

  if (builder) {
    switch (type) {
      case "select":
        return (
          <Select
            value={returnValue()}
            onChange={(value) => {
              setView(path, value);
            }}
            options={options}
          />
        );
      case "textarea":
        return (
          <TextArea
            value={returnValue()}
            onChange={(value) => {
              setView(path, value);
            }}
          />
        );
      case "image":
        return (
          <Image
            value={returnValue()}
            onChange={(value) => {
              setView(path, value);
            }}
          />
        );
      case "table":
        return (
          <Table
            value={returnValue()}
            onChange={(value) => {
              setView(path, value);
            }}
          />
        );
      case "editor":
        return (
          <Editor
            value={returnValue()}
            onChange={(value) => {
              setView(path, value);
            }}
          />
        );
      case "code":
        return (
          <Code
            value={returnValue()}
            onChange={(value) => {
              setView(path, value);
            }}
          />
        );
      default:
        return (
          <Input
            placeholder={placeholder}
            align={align}
            type={type}
            value={returnValue()}
            onChange={(value) => {
              setView(path, value);
            }}
          />
        );
    }
  } else {
    return value !== null
      ? children
      : children
      ? htmlParser(String(children))
      : null;
  }
};

/**
 * Group
 */
class GroupProps {
  path?: string;
  children?: any;
  inline?: boolean;
  align?: "start" | "center" | "end";
  provided?: any;
  index?: number;
}
const Group = ({
  inline = false,
  path = "",
  children = null,
  align = "end",
  index = 0,
}: GroupProps) => {
  let { builder = false } = useSelector((state: any) => state.builderReducer);
  let [hover, setHover]: any = useState(null);

  if (builder)
    return (
      <Draggable draggableId={path} index={index}>
        {(provided: any, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={
              "builder-group " +
              (inline ? " inline" : "") +
              (hover ? " builder-" + hover : "")
            }
          >
            <div
              className="builder-bar"
              style={{
                justifyContent: align == "center" ? align : "flex-" + align,
              }}
            >
              <div
                className="builder-bar-btn text-primary"
                onMouseOver={() => setHover("move")}
                onMouseOut={() => setHover(null)}
                {...provided.dragHandleProps}
              >
                <i className="fa fa-arrows-alt" />
              </div>
              <div
                className="builder-bar-btn text-danger"
                onClick={() => removeView(path)}
                onMouseOver={() => setHover("close")}
                onMouseOut={() => setHover(null)}
              >
                <i className="fa fa-close" />
              </div>
            </div>
            {children}
          </div>
        )}
      </Draggable>
    );
  else return children;
};

/**
 * Section
 */
class SectionProps {
  order?: number;
  children?: any;
  dragHandle?: any;
}
const Section = ({ order = 0, children = null }: SectionProps) => {
  let { view, builder = false } = useSelector(
    (state: any) => state.builderReducer
  );
  let [hover, setHover]: any = useState(null);
  if (builder)
    return (
      <div className={"builder-section" + (hover ? " builder-" + hover : "")}>
        <div className="builder-bar">
          {order > 0 ? (
            <div
              className="builder-bar-btn text-primary"
              onMouseOver={() => setHover("move")}
              onMouseOut={() => setHover(null)}
              onClick={() => moveLine(order, -1)}
            >
              <i className="fa fa-arrow-up" />
            </div>
          ) : null}
          {order + 1 < view.data.length ? (
            <div
              className="builder-bar-btn text-primary"
              onMouseOver={() => setHover("move")}
              onMouseOut={() => setHover(null)}
              onClick={() => moveLine(order, +1)}
            >
              <i className="fa fa-arrow-down" />
            </div>
          ) : null}
          <div
            className="builder-bar-btn text-danger"
            onClick={() => removeLine(order)}
            onMouseOver={() => setHover("close")}
            onMouseOut={() => setHover(null)}
          >
            <i className="fa fa-close" />
          </div>
        </div>
        {children}
      </div>
    );
  else return children;
};
/**
 * Separator
 */
class SeparatorProps {
  order?: number;
  value?: any;
  buttonSize?: number;
  borderSize?: number;
}
const Separator = ({
  buttonSize = 26,
  borderSize = 3,
  order = 0,
}: SeparatorProps) => {
  let { builder = false } = useSelector((state: any) => state.builderReducer);
  if (builder)
    return (
      <div
        className="builder-sperator"
        style={{ borderWidth: borderSize }}
        onClick={() => addLine(order)}
      >
        <button
          style={{
            width: buttonSize,
            height: buttonSize,
            marginTop: -buttonSize / 2,
            marginLeft: -buttonSize / 2,
          }}
        >
          <i className="fa fa-plus" style={{ fontSize: buttonSize - 6 }} />
        </button>
      </div>
    );
  else return null;
};

/**
 * Status
 */
class StatusProps {
  children?: any;
  builder?: any;
}
const Status = ({ children = null, builder = null }: StatusProps) => {
  let { builder: builderStatus = false } = useSelector(
    (state: any) => state.builderReducer
  );
  if (builderStatus) return builder;
  else return children;
};

/**
 * Get Last Number
 */

export const getNumber = (object: any, name: string, additional: number) => {
  let last = Object.keys(object).filter(
    (key) => key.slice(0, key.indexOf("$")) == name
  );
  let last_item = last[last.length - 1];
  return (
    (last_item ? Number(last_item.slice(last_item.indexOf("$") + 1)) : 0) +
    additional
  );
};

/**
 * Builder Menu
 */

const Menu = () => {
  let { builder: builderStatus = false } = useSelector(
    (state: any) => state.builderReducer
  );
  return (
    <Droppable
      droppableId="elements"
      isDropDisabled={true}
      type="droppableItem"
    >
      {(provided, snapshot) => (
        <div className="builder-menu" ref={provided.innerRef}>
          <div className="builder-menu-bar">
            <div></div>
            <div>
              <button
                className={
                  "btn btn-sm btn-" + (builderStatus ? "primary" : "secondary")
                }
                onClick={() => builderMode(!builderStatus)}
              >
                Builder Modu
              </button>
            </div>
          </div>

          <div className="builder-menu-items">
            {ELEMENTS.map((item, key) => (
              <Draggable draggableId={item.element} index={key}>
                {(provided: any, snapshot) => (
                  <Fragment>
                    <div
                      className={
                        "builder-menu-item" +
                        (snapshot.isDragging ? " drag" : "")
                      }
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <i className={"fas fa-" + item.icon} />
                      <span>{item.name}</span>
                    </div>
                    {snapshot.isDragging ? (
                      <div className="builder-menu-item clone">
                        <i className={"fas fa-" + item.icon} />
                        <span>{item.name}</span>
                      </div>
                    ) : null}
                  </Fragment>
                )}
              </Draggable>
            ))}
          </div>
        </div>
      )}
    </Droppable>
  );
};

export default {
  Area,
  Group,
  Section,
  Separator,
  Status,
  Menu,
};
